import type { Session } from '@supabase/supabase-js'
import { getSupabase } from '../../lib/supabase/client'
import { AppError, normalizeError } from '../../lib/errors'
import type { AccessState, Batch, MfaState } from '../../types/domain'
import type { LoginValues, RegisterValues } from './validation'

export async function getSession(): Promise<Session | null> {
  const { data, error } = await getSupabase().auth.getSession()
  if (error) throw normalizeError(error)
  return data.session
}

export async function getAccessState(): Promise<AccessState> {
  const { data, error } = await getSupabase().schema('api').rpc('get_my_access_state')
  if (error) throw normalizeError(error)
  const row = Array.isArray(data) ? data[0] : data
  if (!row) throw normalizeError({ status: 404 })
  return {
    userId: row.user_id,
    profileId: row.profile_id ?? null,
    email: row.email,
    displayName: row.display_name,
    accountStatus: row.account_status,
    roles: row.roles ?? [],
    applicationStatus: row.application_status,
    rejectionReason: row.rejection_reason,
    adminScope: row.admin_scope ?? null,
    adminBatchId: row.admin_batch_id ?? null,
    adminBatchYear: row.admin_batch_year == null ? null : Number(row.admin_batch_year),
    adminAccessStatus: row.admin_access_status ?? null,
    canReviewMemberships: Boolean(row.can_review_memberships),
    canAccessAdmin: Boolean(row.can_access_admin),
    canManageGlobalOperations: Boolean(row.can_manage_global_operations),
    canManageOperationsAdmins: Boolean(row.can_manage_operations_admins),
    canManageBatchAdmins: Boolean(row.can_manage_batch_admins),
    canManageAllCommunities: Boolean(row.can_manage_all_communities),
    canPublishAnnouncements: Boolean(row.can_publish_announcements),
    canViewSystemAudit: Boolean(row.can_view_system_audit),
  }
}

export async function listBatches(): Promise<Batch[]> {
  const { data, error } = await getSupabase()
    .from('batches')
    .select('id,name,year,description')
    .order('year', { ascending: false })
  if (error) throw normalizeError(error)
  return data ?? []
}

export async function login(values: LoginValues) {
  const { data, error } = await getSupabase().auth.signInWithPassword(values)
  if (error) throw normalizeError(error)
  return data
}

export async function register(values: RegisterValues) {
  const { data, error } = await getSupabase().auth.signUp({
    email: values.email,
    password: values.password,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/confirm`,
      data: {
        full_name: values.fullName,
        member_type: values.memberType,
        batch_id: values.memberType === 'student' ? values.batchId : null,
        consent_at: new Date().toISOString(),
      },
    },
  })
  if (error) throw normalizeError(error)
  if (data.user && data.user.identities?.length === 0) {
    throw new AppError(
      'VALIDATION_ERROR',
      'E-mel ini telah didaftarkan. Log masuk atau gunakan pemulihan kata laluan.',
      'email',
    )
  }
  return data
}

export async function sendPasswordReset(email: string) {
  const { error } = await getSupabase().auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  })
  if (error) throw normalizeError(error)
}

export async function updatePassword(password: string) {
  const { error } = await getSupabase().auth.updateUser({ password })
  if (error) throw normalizeError(error)
}

export async function requestPasswordChangeOtp() {
  const { error } = await getSupabase().auth.reauthenticate()
  if (error) throw normalizeError(error)
}

export async function changePasswordWithOtp(password: string, nonce: string) {
  const { error } = await getSupabase().auth.updateUser({ password, nonce })
  if (error) throw normalizeError(error)
  const { error: signOutError } = await getSupabase().auth.signOut({ scope: 'global' })
  if (signOutError) throw normalizeError(signOutError)
}

export async function confirmEmail(tokenHash: string, type: 'email' | 'signup' | 'invite') {
  const { error } = await getSupabase().auth.verifyOtp({ token_hash: tokenHash, type })
  if (error) throw normalizeError(error)
}

export async function getMfaState(): Promise<MfaState> {
  const { data, error } = await getSupabase().auth.mfa.getAuthenticatorAssuranceLevel()
  if (error) throw normalizeError(error)
  return {
    currentLevel: data.currentLevel,
    nextLevel: data.nextLevel,
    required: data.currentLevel === 'aal1' && data.nextLevel === 'aal2',
  }
}

export async function listTotpFactors() {
  const { data, error } = await getSupabase().auth.mfa.listFactors()
  if (error) throw normalizeError(error)
  return data.totp
}

export async function enrollTotp() {
  const { data, error } = await getSupabase().auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: 'SAMBEST Alumni Federation',
  })
  if (error) throw normalizeError(error)
  return data
}

export async function verifyTotp(factorId: string, code: string) {
  const { error } = await getSupabase().auth.mfa.challengeAndVerify({ factorId, code })
  if (error) throw normalizeError(error)
}

export async function unenrollTotp(factorId: string) {
  const { error } = await getSupabase().auth.mfa.unenroll({ factorId })
  if (error) throw normalizeError(error)
}

export async function confirmRecovery(tokenHash: string) {
  const { data, error } = await getSupabase().auth.verifyOtp({
    token_hash: tokenHash,
    type: 'recovery',
  })
  if (error) throw normalizeError(error)
  return data.session
}

export async function sendRegistrationReceived() {
  const { data, error } = await getSupabase().functions.invoke('send-registration-received', {
    body: {},
  })
  if (error) throw normalizeError(error)
  if (!data?.ok)
    throw normalizeError({ status: data?.status ?? 500, message: data?.error?.message })
  return data as { ok: true; email: { sent: boolean; warning?: string } }
}

export async function logout() {
  const { error } = await getSupabase().auth.signOut()
  if (error) throw normalizeError(error)
}
